import React, { useState, useEffect } from 'react';
import { Calendar, FileText, CheckCircle, Send, ArrowRight, Trash2, RotateCcw, Ban } from 'lucide-react';
import { useResponsive } from '../hooks/useResponsive';
import { formatDisplayDate } from './DateFilter';

const API = process.env.REACT_APP_BACKEND_API_URL;
// readOnlyActions: when true, forward-move buttons (Accept Task, Move to WIP,
// Move to Ready To Submit, Submit Task) are hidden entirely — the card is
// view-only. Used for Team Lead, since only the assigned Recruiter should
// progress a task through these stages; the Team Lead's job is approval
// (handled separately in TaskDetailModel) and reassignment (below), not
// moving the recruiter's own work forward.
// canActOnTask(task): optional per-card gate for those same workflow buttons.
// When given it REPLACES readOnlyActions, so a Team Lead can progress only the
// tasks assigned to themselves (self-assigned, or assigned to them by an Admin)
// while tasks belonging to Recruiters stay view-only.
// onEndTask(taskId, reason): Admin only — ends a not-yet-completed task.
// onReassignTask: only passed for Team Lead/Admin — enables the Reassign
// Task button on Completed cards (item #3).
const KanbanBoard = ({ tasks = [], filterCondition = () => true, onSelectTask, onUpdateTaskStatus, readOnlyActions = false, onDeleteTask, onReassignTask, recruiterOptions = [], canActOnTask, onEndTask }) => {
    const { isMobile, isTablet } = useResponsive();
    const [submittingTaskId, setSubmittingTaskId] = useState(null);
    const [descriptionInput, setDescriptionInput] = useState('');
    const [submittedToOption, setSubmittedToOption] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Every Team Lead from the existing users data (GET /api/users?role=teamLead) — the "Submitted To" choices.
    const [teamLeadNames, setTeamLeadNames] = useState([]);
    const [reassignOptions, setReassignOptions] = useState([]);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/api/users?roles=recruiter,teamLead`)
            .then(res => res.json())
            .then(data => {
                if (cancelled) return;

                const names = Array.isArray(data)
                    ? data
                        .filter(u => ['recruiter', 'teamlead'].includes(
                            (u.role || '').trim().toLowerCase()
                        ))
                        .map(u => u.name)
                        .filter(Boolean)
                    : [];

                setReassignOptions(names);
            })
            .catch(err => console.error('Failed to fetch reassignment users:', err));

        return () => {
            cancelled = true;
        };
    }, []);
    const [reassigningTaskId, setReassigningTaskId] = useState(null);
    const [reassignRecruiter, setReassignRecruiter] = useState('');
    const [endingTaskId, setEndingTaskId] = useState(null);
    const [endReason, setEndReason] = useState('');

    const filteredTasks = tasks.filter(filterCondition);

    // Item #1: exactly 5 statuses, exactly 5 columns, in this order.
    // Assigned Task and Pending Tasks are DISTINCT columns — a brand-new task
    // always lands in Assigned, never Pending. Pending only exists for tasks
    // a Team Lead/Admin has reassigned from Completed.
    const getEffectiveStatus = (task) =>
        (task._historicalStatus || task.effective_status || task.status || '').trim().toLowerCase();

    const getEffectiveAssignedTo = (task) =>
        task._historicalAssignedTo || task.effective_assigned_to || task.assigned_to || '';

    const getEffectiveReassignmentCount = (task) =>
        task.effective_reassignment_count ?? task.reassignment_count ?? 0;

    const assignedTasks = filteredTasks.filter(t => getEffectiveStatus(t) === 'assigned');
    const pendingTasks = filteredTasks.filter(t => getEffectiveStatus(t) === 'pending');
    const wipTasks = filteredTasks.filter(t => getEffectiveStatus(t) === 'work in progress');
    const readyToSubmitTasks = filteredTasks.filter(t => getEffectiveStatus(t) === 'ready to submit');
    const completedTasks = filteredTasks.filter(t => getEffectiveStatus(t) === 'completed');

    const columnStyle = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '500px', boxSizing: 'border-box' };
    const columnHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #2563eb', paddingBottom: '8px', fontSize: '13px', fontWeight: '700', color: '#1f2937' };
    const badgeStyle = { background: '#e2e8f0', color: '#334155', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' };

    // A task is "awaiting approval" once it's been submitted (submission
    // fields filled) while still sitting in Ready To Submit — there's no
    // separate "Submitted" status/column anymore; Team Lead's approval moves
    // it straight from Ready To Submit to Completed.
    const isAwaitingApproval = (task) => !!(task.submission_description && task.submitted_to);

    // "Submitted To" choices: Client + every Team Lead (+ the person who assigned the task when that is not
    // a Team Lead, e.g. an Admin, so existing tasks keep working). Same list for normal and reassigned tasks.
    const submitToChoices = (task) => {
        const names = [...teamLeadNames];
        if (task.assigned_by && !names.some(n => n.trim().toLowerCase() === task.assigned_by.trim().toLowerCase())) names.push(task.assigned_by);
        return names;
    };

    const closeSubmitForm = () => {
        setSubmittingTaskId(null);
        setDescriptionInput('');
        setSubmittedToOption('');
        setSubmitError('');
    };

    const handleFinalSubmit = async (task) => {
        if (!descriptionInput.trim()) {
            setSubmitError('Please provide a description of what you did in this task.');
            return;
        }
        if (!submittedToOption) {
            setSubmitError('Please select Submitted To.');
            return;
        }
        setSubmitError('');
        setSubmitting(true);
        try {
            // Status is intentionally omitted — the task stays "Ready To Submit"
            // (now awaiting approval) until the selected Team Lead / Admin approves it,
            // which is the only path to Completed. The backend re-validates everything.
            await onUpdateTaskStatus(task.id, null, descriptionInput.trim(), submittedToOption);
            closeSubmitForm();
        } catch (err) {
            // The page-level handler already alerted the server's message; keep the form open so nothing is lost.
            setSubmitError(err?.message || 'Failed to submit task.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmReassign = (task) => {
        if (!reassignRecruiter) {
            alert('Please select a recruiter.');
            return;
        }
        onReassignTask(task.id, reassignRecruiter);
        setReassigningTaskId(null);
        setReassignRecruiter('');
    };

    // Item #1/#2: NEW TASK / REASSIGNED TASK / PENDING TASK badges.
    // - Pending is now used ONLY for a WIP task that went stale (item #1's
    //   Pending Task Logic) — so status === 'Pending' always means PENDING TASK.
    // - A reassigned task lands back in Assigned (not Pending), so within the
    //   Assigned column: reassignment_count > 0 -> REASSIGNED TASK, otherwise NEW TASK.
    const getTaskTag = (task) => {
        const status = getEffectiveStatus(task);
        const reassignmentCount = getEffectiveReassignmentCount(task);
        if (status === 'pending') { return { label: 'PENDING TASK', bg: '#fee2e2', color: '#b91c1c' }; }
        if (status === 'assigned') {
            return reassignmentCount > 0
                ? { label: 'REASSIGNED TASK', bg: '#fef3c7', color: '#92400e' }
                : { label: 'NEW TASK', bg: '#dcfce7', color: '#166534' };
        }
        return null;
    };

    const renderCard = (task, columnType) => {
        const tag = getTaskTag(task);
        const canAct = canActOnTask ? !!canActOnTask(task) : !readOnlyActions;
        const adminEnded = columnType === 'completed' && !!task.admin_ended;
        return (
            <div
                key={task.id}
                data-admin-ended={adminEnded ? 'true' : undefined}
                onClick={() => onSelectTask(task)}
                style={{
                    background: adminEnded ? '#f5f3ff' : '#fff', padding: '12px', borderRadius: '6px',
                    boxShadow: adminEnded ? '0 0 0 2px rgba(109,40,217,0.18)' : '0 1px 2px rgba(0,0,0,0.04)', cursor: 'pointer',
                    border: adminEnded ? '2px solid #6d28d9' : '1px solid #e2e8f0',
                    display: 'flex', flexDirection: 'column', gap: '6px', boxSizing: 'border-box'
                }}
            >
                {adminEnded && (
                    <span style={{
                        alignSelf: 'flex-start', fontSize: '9px', fontWeight: '800', letterSpacing: '0.04em',
                        padding: '2px 7px', borderRadius: '10px', background: '#6d28d9', color: '#fff', whiteSpace: 'nowrap'
                    }}>
                        {task?.ended_by}
                    </span>
                )}
                {/* Item #2: tag badge — small, high-contrast, and just a flex item so
                it reflows naturally at any card width (mobile/tablet/desktop). */}
                {tag && (
                    <span style={{
                        alignSelf: 'flex-start', fontSize: '9px', fontWeight: '800', letterSpacing: '0.03em',
                        padding: '2px 7px', borderRadius: '10px', background: tag.bg, color: tag.color, whiteSpace: 'nowrap'
                    }}>
                        {tag.label}
                    </span>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Item #2: display-only — shows the Role Name, not the Task ID. The real
                    Task ID (task.display_id) is untouched everywhere else (delete confirm, etc). */}
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2563eb' }}>Role: {task.job_role || task.title}</span>
                    <span style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{task.company}</span>
                </div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', lineHeight: '1.3' }}>Task: {task.title}</div>
                <div style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={11} /> {formatDisplayDate(task.assigned_date)}
                </div>
                {getEffectiveReassignmentCount(task) === 0 && (
                    <div style={{ fontSize: '10px', color: '#166534', background: '#fef3c7', padding: '3px 6px', borderRadius: '4px' }}>
                        Task Assigned By {task.assigned_by} to {getEffectiveAssignedTo(task)} on {formatDisplayDate(task.assigned_date)}
                    </div>
                )}
                {getEffectiveReassignmentCount(task) > 0 && (
                    // Item #2: "Reassigned 1x — by Admin User on 2026-09-28 to: [Assigned Name]"
                    <div style={{ fontSize: '10px', color: '#92400e', background: '#fef3c7', padding: '3px 6px', borderRadius: '4px' }}>
                        Task Reassigned {task.reassignment_count}x By {task.reassigned_by} to {getEffectiveAssignedTo(task)} on {formatDisplayDate(task.reassigned_date)}
                    </div>
                )}
                {task.instructions && (
                    <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #f1f5f9', fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                        <span style={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}><FileText size={10} /> Instructions:</span>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {task.instructions}
                        </div>
                    </div>
                )}

                {onDeleteTask && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete task ${task.display_id}? This cannot be undone.`)) {
                                onDeleteTask(task.id);
                            }
                        }}
                        style={{
                            marginTop: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none',
                            padding: '5px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                        }}
                    >
                        <Trash2 size={12} /> Delete Task
                    </button>
                )}

                {canAct && columnType === 'pending' && (
                    // Pending Task Flow: recruiter clicks Accept Task to move straight
                    // to Work In Progress — Pending no longer routes back through
                    // Assigned (everything else about the Assigned column is unchanged).
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTaskStatus(task.id, 'Work In Progress');
                        }}
                        style={{
                            marginTop: '6px', background: '#7c3aed', color: '#fff', border: 'none',
                            padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                        }}
                    >
                        <CheckCircle size={12} /> Accept Task
                    </button>
                )}

                {canAct && columnType === 'assigned' && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTaskStatus(task.id, 'Work In Progress');
                        }}
                        style={{
                            marginTop: '6px', background: '#2563eb', color: '#fff', border: 'none',
                            padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                        }}
                    >
                        <CheckCircle size={12} /> Accept Task
                    </button>
                )}

                {canAct && columnType === 'wip' && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTaskStatus(task.id, 'Ready To Submit');
                        }}
                        style={{
                            marginTop: '6px', background: '#f59e0b', color: '#fff', border: 'none',
                            padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                        }}
                    >
                        <ArrowRight size={12} /> Move to Ready To Submit
                    </button>
                )}

                {columnType === 'readyToSubmit' && isAwaitingApproval(task) && (
                    <div style={{ marginTop: '6px', background: '#dbeafe', color: '#1e40af', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', textAlign: 'center' }}>
                        Awaiting Approval
                    </div>
                )}
                {columnType === 'readyToSubmit' && isAwaitingApproval(task) && (
                    <div data-testid="submission-summary" style={{ marginTop: '6px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '6px 8px', fontSize: '11px', color: '#14532d', textAlign: 'left' }}>
                        <div><strong>Submitted To:</strong> {task.submitted_to}</div>
                        <div style={{ marginTop: '3px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong>Description:</strong> {task.submission_description}</div>
                    </div>
                )}
                {canAct && columnType === 'readyToSubmit' && !isAwaitingApproval(task) && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {submittingTaskId === task.id ? (
                            <>
                                {submitError && (
                                    <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '5px 8px', borderRadius: '4px', fontSize: '11px' }}>{submitError}</div>
                                )}
                                <textarea
                                    placeholder="Describe what you did..."
                                    value={descriptionInput}
                                    onChange={(e) => setDescriptionInput(e.target.value)}
                                    rows={2}
                                    style={{ width: '100%', fontSize: '11px', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <label style={{ fontSize: '10px', fontWeight: '700', color: '#475569' }}>Submitted To:</label>
                                    <select
                                        value={submittedToOption}
                                        onChange={(e) => setSubmittedToOption(e.target.value)}
                                        style={{ width: '100%', fontSize: '11px', padding: '5px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff' }}
                                    >
                                        <option value="">Select recipient...</option>
                                        <option value="Client">Client</option>
                                        {submitToChoices(task).map(name => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                                    <button
                                        onClick={() => handleFinalSubmit(task)}
                                        disabled={submitting}
                                        style={{ flex: 1, background: '#16a34a', color: '#fff', border: 'none', padding: '5px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
                                    >
                                        {submitting ? 'Submitting...' : 'Confirm Submit'}
                                    </button>
                                    <button
                                        onClick={closeSubmitForm}
                                        style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '5px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <button
                                onClick={() => {
                                    setSubmittingTaskId(task.id);
                                    setDescriptionInput('');
                                    setSubmittedToOption('');
                                    setSubmitError('');
                                }}
                                style={{
                                    background: '#16a34a', color: '#fff', border: 'none',
                                    padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                }}
                            >
                                <Send size={12} /> Submit Task
                            </button>
                        )}
                    </div>
                )}

                {onEndTask && columnType !== 'completed' && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '4px' }}>
                        {endingTaskId === task.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <textarea
                                    placeholder="End reason (optional)"
                                    value={endReason}
                                    onChange={(e) => setEndReason(e.target.value)}
                                    rows={2}
                                    style={{ width: '100%', fontSize: '11px', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        onClick={() => { onEndTask(task.id, endReason.trim()); setEndingTaskId(null); setEndReason(''); }}
                                        style={{ flex: 1, background: '#6d28d9', color: '#fff', border: 'none', padding: '5px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                    >
                                        Confirm End Task
                                    </button>
                                    <button
                                        onClick={() => { setEndingTaskId(null); setEndReason(''); }}
                                        style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '5px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => { setEndingTaskId(task.id); setEndReason(''); }}
                                style={{
                                    background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd',
                                    padding: '5px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%'
                                }}
                            >
                                <Ban size={12} /> End Task
                            </button>
                        )}
                    </div>
                )}

                {onReassignTask && columnType === 'completed' && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '6px' }}>
                        {reassigningTaskId === task.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <select
                                    value={reassignRecruiter}
                                    onChange={(e) => setReassignRecruiter(e.target.value)}
                                    style={{ width: '100%', fontSize: '11px', padding: '5px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff' }}
                                >
                                    <option value="">Select assignee...</option>
                                    {reassignOptions.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        onClick={() => handleConfirmReassign(task)}
                                        style={{ flex: 1, background: '#7c3aed', color: '#fff', border: 'none', padding: '5px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                    >
                                        Confirm Reassign
                                    </button>
                                    <button
                                        onClick={() => { setReassigningTaskId(null); setReassignRecruiter(''); }}
                                        style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '5px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => { setReassigningTaskId(task.id); setReassignRecruiter(task.assigned_to || ''); }}
                                style={{
                                    background: '#7c3aed', color: '#fff', border: 'none',
                                    padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%'
                                }}
                            >
                                <RotateCcw size={12} /> Reassign Task
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))',
            gap: '14px', width: '100%', boxSizing: 'border-box'
        }}>
            <div style={columnStyle}>
                <div style={columnHeaderStyle}>
                    <span>1. Assigned Task</span>
                    <span style={badgeStyle}>{assignedTasks.length}</span>
                </div>
                {assignedTasks.map(t => renderCard(t, 'assigned'))}
            </div>

            <div style={columnStyle}>
                <div style={columnHeaderStyle}>
                    <span>2. Pending Tasks</span>
                    <span style={badgeStyle}>{pendingTasks.length}</span>
                </div>
                {pendingTasks.map(t => renderCard(t, 'pending'))}
            </div>

            <div style={columnStyle}>
                <div style={columnHeaderStyle}>
                    <span>3. Work In Progress</span>
                    <span style={badgeStyle}>{wipTasks.length}</span>
                </div>
                {wipTasks.map(t => renderCard(t, 'wip'))}
            </div>

            <div style={columnStyle}>
                <div style={columnHeaderStyle}>
                    <span>4. Ready To Submit</span>
                    <span style={badgeStyle}>{readyToSubmitTasks.length}</span>
                </div>
                {readyToSubmitTasks.map(t => renderCard(t, 'readyToSubmit'))}
            </div>

            <div style={columnStyle}>
                <div style={columnHeaderStyle}>
                    <span>5. Completed Tasks</span>
                    <span style={badgeStyle}>{completedTasks.length}</span>
                </div>
                {completedTasks.map(t => renderCard(t, 'completed'))}
            </div>
        </div>
    );
};

export default KanbanBoard;